import { AlternateFlavor, AlternateFlavorConfig } from '@portal/processes/ShopV2/ShopV2.types';
import FlavorSelection from '@portal/shared/components/FlavorSelection/FlavorSelection';
import { useProductStore } from '@portal/state/store/pdpStore';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { FlavorSelector as FlavorSelectorProps, SanityKeyed } from 'types/studio/studio';
import { FLAVOR_CONFIG } from '@portal/constants/flavors';
import { CommonSectionProps } from '../../types';
import { getUniqueVariantId } from '../utils';
import styles from './FlavorSelector.module.scss';

const FlavorSelector = (props: SanityKeyed<FlavorSelectorProps> & CommonSectionProps) => {
  const { _key: key, title, flavors, outOfStockText } = props;
  const router = useRouter();

  const selectedFlavor = useProductStore((state) => state.flavor);
  const setSelectedFlavor = useProductStore((state) => state.setFlavor);
  const setSelectedFlavorSubtitle = useProductStore((state) => state.setFlavorSubtitle);
  const selectedVariantId = useProductStore((state) => state.selectedVariantId);
  const setSelectedVariantId = useProductStore((state) => state.setSelectedVariantId);
  const variants = useProductStore((state) => state.variants);
  const setCheckoutDisabled = useProductStore((state) => state.setCheckoutDisabled);
  // Map Sanity flavor config to the structure expected by FlavorSelection
  const mappedFlavorVariants: AlternateFlavorConfig[] = useMemo(() => {
    const mappedColors = {
      green: FLAVOR_CONFIG.default.color, // Original AG1 flavor
      yellow: FLAVOR_CONFIG.tropical.color, // Tropical flavor
      orange: FLAVOR_CONFIG.citrus.color, // Citrus flavor
      pink: FLAVOR_CONFIG.berry.color, // AG1 Berry flavor
      agzChocolate: FLAVOR_CONFIG.chocolate.color, // AGZ Chocolate flavor
      agzChocolateMint: FLAVOR_CONFIG.chocolateMint.color, // AGZ Chocolate Mint flavor
      agzMixedBerry: FLAVOR_CONFIG.mixedBerry.color // AGZ Mixed Berry flavor
    };

    return (flavors || [])
      .filter(
        (
          flavor
        ): flavor is typeof flavor & {
          title: string;
          color: string;
          outOfStock: boolean;
          flavorMessage: [];
          _key: string;
        } =>
          typeof flavor.title === 'string' &&
          typeof flavor.color === 'string' &&
          typeof flavor._key === 'string'
      )
      .map((flavor) => {
        const flavorColor = mappedColors[flavor.color] || flavor.color;
        const flavorKey = Object.keys(FLAVOR_CONFIG).find(
          (configKey) => FLAVOR_CONFIG[configKey].color === flavorColor
        );

        let fontColor = 'black';
        if (flavorKey) {
          fontColor = FLAVOR_CONFIG[flavorKey].fontColor;
        } else if (flavor.title === 'Original') {
          fontColor = 'white';
        }

        return {
          title: flavor.title,
          color: flavorColor,
          hoverColor: '',
          id: flavor.queryParam,
          fontColor,
          outOfStock: flavor.outOfStock,
          flavorMessage: flavor.flavorMessage
        };
      });
  }, [flavors]);

  useEffect(() => {
    if (!router.isReady || !flavors?.length) return;

    const queryMatchVariant = flavors.find(
      (flavor) => flavor?.queryParam && flavor?.queryParam === router.query?.flavor
    );

    let flavorToSet: string;
    if (queryMatchVariant) {
      const subtitleText = queryMatchVariant.flavorSubtitle
        ?.map((block) => block.children.map((child: { text: any }) => child.text).join(''))
        .join(' ');

      flavorToSet = queryMatchVariant.queryParam;
      setSelectedFlavor(flavorToSet);
      setSelectedFlavorSubtitle(subtitleText);
    } else if (router.query?.flavor) {
      // Use URL flavor parameter directly if no CMS config found
      flavorToSet = router.query.flavor as string;
      setSelectedFlavor(flavorToSet);
      setSelectedFlavorSubtitle('');
    } else {
      const subtitleText = flavors[0]?.flavorSubtitle
        ?.map((block) => block.children.map((child: { text: any }) => child.text).join(''))
        .join(' ');

      flavorToSet = flavors[0]?.queryParam;
      setSelectedFlavor(flavorToSet);
      setSelectedFlavorSubtitle(subtitleText);
      setCheckoutDisabled(flavors[0]?.outOfStock || false);
    }

    // Set selectedVariantId as fallback for pages without QuantitySelectorCards
    if (variants?.length) {
      const correctVariantId = getUniqueVariantId(variants[0], flavorToSet);

      // Only set if empty OR if the current ID doesn't exist in our variants
      // This handles cached incorrect IDs while preserving valid ones from QuantitySelectorCards
      const isValidId =
        selectedVariantId &&
        variants.some((variant) => {
          const variantId = getUniqueVariantId(variant, flavorToSet);
          return variantId === selectedVariantId;
        });

      if (correctVariantId && (!selectedVariantId || !isValidId)) {
        setSelectedVariantId(correctVariantId);
      }
    }
  }, [
    flavors,
    router.isReady,
    router.query,
    variants,
    selectedVariantId,
    setSelectedFlavor,
    setSelectedFlavorSubtitle,
    setSelectedVariantId,
    setCheckoutDisabled
  ]);

  if (!title || !flavors?.length) return null;

  const handleFlavorClick = (flavor: AlternateFlavor | AlternateFlavorConfig) => {
    const flavorQueryParam = flavor.id;
    const newQuery: Record<string, string> = {
      ...(router.query as Record<string, string>),
      flavor: flavorQueryParam
    };

    if (variants && variants.length > 0) {
      if (selectedVariantId) {
        const currentQty = Number(selectedVariantId.split('-')?.[1]);

        // Use the actual quantity value for the URL parameter
        if (variants[currentQty - 1]) {
          newQuery.quantity = String(variants[currentQty - 1].quantity);
        }
      } else if (variants[0]) {
        // Default to first variant's quantity
        newQuery.quantity = String(variants[0].quantity);
      }
    }

    setCheckoutDisabled(flavor?.outOfStock || false);

    router.replace(
      {
        pathname: router.pathname,
        query: newQuery
      },
      undefined,
      { shallow: true }
    );
  };

  const validFlavors = mappedFlavorVariants.filter(
    (flavor): flavor is AlternateFlavorConfig =>
      typeof flavor.title === 'string' &&
      typeof flavor.color === 'string' &&
      typeof flavor.id === 'string'
  );

  if (validFlavors.length === 0 || !router.isReady) {
    return null;
  }

  return (
    <div className={styles.container} id={key}>
      <FlavorSelection
        outOfStockText={outOfStockText || ''}
        flavorVariants={validFlavors}
        activeVariantId={selectedFlavor}
        onChange={handleFlavorClick}
      />
    </div>
  );
};

export default FlavorSelector;
